import { useMemo } from 'react';
import ReactFlow, { Background, Controls, MiniMap, Handle, Position } from 'reactflow';
import dagre from 'dagre';
import 'reactflow/dist/style.css';

const NODE_WIDTH = 180;
const NODE_HEIGHT = 60;

const formatShape = (shape) => {
    if (!shape || shape.length === 0) return null;
    return `[${shape.map(d => d == null ? '?' : d).join(', ')}]`;
};

const ShapeNode = ({ data }) => (
    <div
        style={{
            width: NODE_WIDTH,
            height: NODE_HEIGHT,
            border: '1px solid #c4b5fd',
            background: '#f5f3ff',
            borderRadius: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '4px 6px',
            textAlign: 'center',
            overflow: 'hidden',
        }}
    >
        <Handle type='target' position={Position.Top} />
        <div style={{ fontSize: 11, fontWeight: 500, lineHeight: 1.2 }}>{data.opType}</div>
        {data.shape && (
            <div style={{ fontSize: 9, color: '#6b7280', fontFamily: 'monospace', marginTop: 2 }}>
                {data.shape}
            </div>
        )}
        <Handle type='source' position={Position.Bottom} />
    </div>
);

const nodeTypes = { shape: ShapeNode };

const layout = (nodes, edges) => {
    const g = new dagre.graphlib.Graph();
    g.setGraph({ rankdir: 'TB', nodesep: 30, ranksep: 50 });
    g.setDefaultEdgeLabel(() => ({}));

    nodes.forEach(n => g.setNode(n.id, { width: NODE_WIDTH, height: NODE_HEIGHT }));
    edges.forEach(e => g.setEdge(e.source, e.target));

    dagre.layout(g);

    const positioned = nodes.map(n => {
        const pos = g.node(n.id);
        return {
            id: n.id,
            type: 'shape',
            position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 },
            data: {
                opType: n.op_type,
                shape: formatShape(n.shape),
            },
        };
    });

    const flowEdges = edges.map((e, i) => ({
        id: `e${i}`,
        source: e.source,
        target: e.target,
        type: 'smoothstep',
        style: { stroke: '#9ca3af' },
    }));

    return { positioned, flowEdges };
};

const ModelGraph = ({ graph }) => {
    const { positioned, flowEdges } = useMemo(() => {
        if (!graph?.nodes?.length) return { positioned: [], flowEdges: [] };
        return layout(graph.nodes, graph.edges || []);
    }, [graph]);

    if (!positioned.length) {
        return <div className='text-sm text-gray-500'>No graph nodes available.</div>;
    }

    // Fixed canvas; user pans/zooms via reactflow Controls.
    // On mount, place the topmost node (smallest y) at ~1/3 from the top of
    // the viewport so the graph reads downward naturally.
    const CONTAINER_HEIGHT = 600;
    const INITIAL_ZOOM = 0.9;
    const handleInit = (instance) => {
        const topNode = positioned.reduce(
            (best, n) => (n.position.y < best.position.y ? n : best),
            positioned[0],
        );
        // setCenter places (x, y) at screen center; offset by 1/6 of height
        // (= 1/2 − 1/3) in world coords to push the node toward the top third.
        const yOffsetWorld = (CONTAINER_HEIGHT / 6) / INITIAL_ZOOM;
        instance.setCenter(
            topNode.position.x + NODE_WIDTH / 2,
            topNode.position.y + NODE_HEIGHT / 2 + yOffsetWorld,
            { zoom: INITIAL_ZOOM, duration: 0 },
        );
    };

    return (
        <div style={{ width: '100%', height: CONTAINER_HEIGHT, border: '1px solid #e5e7eb', borderRadius: 4 }}>
            <ReactFlow
                nodes={positioned}
                edges={flowEdges}
                nodeTypes={nodeTypes}
                onInit={handleInit}
                proOptions={{ hideAttribution: true }}
                minZoom={0.1}
                maxZoom={2}
            >
                <Background gap={16} />
                <Controls />
                <MiniMap pannable zoomable />
            </ReactFlow>
        </div>
    );
};

export default ModelGraph;
